// ISDP CUSTOM: Catalog Status card — extracted from the (now-removed) admin
// Overview page so the System section can drop it in. Shows the total entity
// count + per-kind breakdown, and offers "Refresh all" (walks every entity and
// calls refreshEntity) plus "Reload stats". Uses the shared admin visual system
// classes. Localized via adminMessages (dashboard.* keys, unchanged).
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { adminMessages } from '../../i18n/admin';
import './AdminShell.css';

// Delay before reloading catalog stats after a refresh, giving the backend a
// moment to finish processing the refresh request.
const STATS_REFRESH_DELAY_MS = 3000;

/**
 * Entity-count "Catalog Status" card. Self-contained: fetches its own stats and
 * owns the refresh flow. Renders as a .devhub-admincard, so it can be placed
 * directly inside any admin section grid.
 */
export const CatalogStatusCard = () => {
  const catalogApi = useApi(catalogApiRef);
  const { t } = useTranslationRef(adminMessages);

  const [entityCount, setEntityCount] = useState<number | null>(null);
  const [kindCounts, setKindCounts] = useState<Record<string, number>>({});
  const [statsLoading, setStatsLoading] = useState(true);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [refreshTotal, setRefreshTotal] = useState(0);
  const [refreshResult, setRefreshResult] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await catalogApi.getEntities({ fields: ['kind'] });
      const entities = response.items;
      setEntityCount(entities.length);
      const counts: Record<string, number> = {};
      for (const e of entities) {
        counts[e.kind] = (counts[e.kind] || 0) + 1;
      }
      setKindCounts(counts);
    } catch {
      setEntityCount(0);
    } finally {
      setStatsLoading(false);
    }
  }, [catalogApi]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleRefreshAll = useCallback(async () => {
    setRefreshing(true);
    setRefreshResult(null);
    setRefreshProgress(0);
    try {
      const response = await catalogApi.getEntities({
        fields: ['metadata.namespace', 'metadata.name', 'kind'],
      });
      const entities = response.items;
      setRefreshTotal(entities.length);
      let refreshed = 0;
      let errors = 0;
      for (const entity of entities) {
        const ns = entity.metadata.namespace || 'default';
        const ref = `${entity.kind.toLowerCase()}:${ns}/${entity.metadata.name}`;
        try {
          await catalogApi.refreshEntity(ref);
        } catch {
          errors++;
        }
        refreshed++;
        setRefreshProgress(refreshed);
      }
      setRefreshResult(
        t('dashboard.refreshDone', {
          refreshed: String(refreshed - errors),
          skipped:
            errors > 0
              ? t('dashboard.refreshSkipped', { n: String(errors) })
              : '',
        }),
      );
      setTimeout(loadStats, STATS_REFRESH_DELAY_MS);
    } catch (err: any) {
      setRefreshResult(t('dashboard.refreshError', { message: err.message }));
    } finally {
      setRefreshing(false);
    }
  }, [catalogApi, loadStats, t]);

  return (
    <div className="devhub-admincard">
      <div className="devhub-admincard__head">
        <odx-icon name="core::tile-menu" size="sm" />
        <h3 className="devhub-admin-t-cardtitle">{t('dashboard.catalogStatus')}</h3>
      </div>

      {statsLoading ? (
        <p className="devhub-admin-t-body">{t('common.loading')}</p>
      ) : (
        <>
          <div className="devhub-admin-t-statvalue">
            {entityCount}{' '}
            <span className="devhub-admin-t-statlabel">
              {entityCount === 1
                ? t('dashboard.entityOne')
                : t('dashboard.entityOther')}
            </span>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, margin: '12px 0' }}>
            {Object.entries(kindCounts)
              .sort(([, a], [, b]) => b - a)
              .map(([kind, count]) => (
                <span key={kind} className="devhub-adminchip devhub-adminchip--stat">
                  {kind}: {count}
                </span>
              ))}
          </div>

          {entityCount === 0 && (
            <div className="devhub-adminchip devhub-adminchip--warn">
              {t('dashboard.catalogEmpty')}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            <odx-button
              variant="primary"
              size="sm"
              type="button"
              disabled={refreshing || undefined}
              onClick={handleRefreshAll}
            >
              {refreshing ? t('dashboard.refreshing') : t('dashboard.refreshAll')}
            </odx-button>
            <odx-button
              variant="secondary"
              size="sm"
              type="button"
              onClick={loadStats}
            >
              {t('dashboard.reloadStats')}
            </odx-button>
          </div>

          {refreshing && refreshTotal > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="devhub-adminmeter__track">
                <div
                  className="devhub-adminmeter__fill"
                  style={{ width: `${(refreshProgress / refreshTotal) * 100}%` }}
                />
              </div>
              <p className="devhub-admin-t-caption" style={{ marginTop: 4 }}>
                {t('dashboard.progressEntities', {
                  done: String(refreshProgress),
                  total: String(refreshTotal),
                })}
              </p>
            </div>
          )}

          {refreshResult && (
            <p className="devhub-admin-t-body" style={{ marginTop: 12 }}>
              {refreshResult}
            </p>
          )}
        </>
      )}
    </div>
  );
};
